import { Component, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { ME_USER } from "app/bundles/authorize/shared/user-profile";
import { PhotosGalleryFormComponent } from "app/bundles/requests/edit-request/photos-gallery-form/photos-gallery-form.component";
import { ReturnDDSDocument } from "app/bundles/requests/shared/models/returnDDSDocument";
import { RequestService } from "app/bundles/requests/shared/services/request.service";
import { CoreService } from "app/core/core.service";
import { PreviousPageService } from "app/core/previous-page.service";
import { StorageService } from "app/shared/services/storage.service";
import {
  Observable,
  Subscription,
  first,
  forkJoin,
  map,
  of,
  switchMap,
  tap,
} from "rxjs";
import { ConfirmDialogService } from "../../../../core/confirm-dialog.service";
import { STATUS_ENUM } from "../../shared/enums/status";
import { IApiErrorSuccessDTO } from "../../shared/models/apiErrorSuccess";
import {
  CollapseItem,
  CollapseItemsEnum,
} from "../../shared/models/collapseItem";
import { Comment } from "../../shared/models/comment";
import {
  IAllowableActivity,
  IAllowableResponsible,
} from "../../shared/models/currentUser";
import { EventRequestById } from "../../shared/models/event";
import { IGeojsonRequest } from "../../shared/models/geojsonRequest";
import {
  IWorkorderOrServiceRequestSummary,
  RequestSourceTypeEnum,
} from "../../shared/models/request";
import { Status } from "../../shared/models/status";
import { isInternalWorkorderRequestPipe } from "../../shared/pipes/is-internal-workorder-request.pipe";
import { CommentService } from "../../shared/services/comment.service";
import { CurrentUserService } from "../../shared/services/current-user.service";
import { EditRequestService } from "../../shared/services/edit-request.service";
import { StatusService } from "../../shared/services/status.service";

interface IRequestIdentifier {
  id: number;
  source: RequestSourceTypeEnum;
}

@Component({
  selector: "app-edit-form",
  templateUrl: "./edit-form.component.html",
  styleUrls: ["./edit-form.component.scss"],
})
export class EditFormComponent implements OnInit, OnDestroy {
  @Input()
  collapseItem: CollapseItem;
  @Input()
  request: IWorkorderOrServiceRequestSummary;
  @Input()
  redirectRoute: string;
  @ViewChild("photosGalleryControl") photosGallery: PhotosGalleryFormComponent;

  public formGroup: UntypedFormGroup;
  public statuses: Status[] = [];
  public comments: Comment[] = [];
  public sectionTitle: string;
  public exitInProgress = false;
  public allowableUsersList: IAllowableResponsible[];
  public allowableActivitiesList: IAllowableActivity[];
  public subscriptions = new Subscription();
  public assignAllowableUserText: string;
  public isInternalWorkorderRequestPipe = new isInternalWorkorderRequestPipe();
  public meUser = ME_USER;
  private collapseItemName: CollapseItemsEnum;

  constructor(
    private fb: UntypedFormBuilder,
    private router: Router,
    private coreService: CoreService,
    private storageService: StorageService,
    private statusService: StatusService,
    private commentService: CommentService,
    private editRequestService: EditRequestService,
    private confirmDialogService: ConfirmDialogService,
    private requestService: RequestService,
    private translateService: TranslateService,
    private currentUserService: CurrentUserService,
    private previousPageService: PreviousPageService,
  ) {
    this.createForm();
  }

  public ngOnInit() {
    this.collapseItemName = this.collapseItem.getName();

    if (this.collapseItem && this.collapseItem.getOnlyCompletedStatus()) {
      // special case in session of closing a workorder.
      this.subscriptions.add(
        this.statusService
          .getStatusByCode("T")
          .subscribe((status) => this.statuses.push(status)),
      );
      // reinitialize status form control and set the validations depending on the status 'Completed'
      const statusControl: UntypedFormControl = <UntypedFormControl>(
        this.formGroup.controls["status"]
      );
      statusControl.setValue("T");
      statusControl.disable();
      this.preprocessReason("T");
      this.setValidationsOnStatuses("T");
    } else if (this.collapseItemName === CollapseItemsEnum.returnDDS) {
      this.preprocessReason("R");
    } else if (this.request.status && this.request.status.code) {
      this.subscriptions.add(
        this.statusService
          .getPossibleStatuses(this.request.status.code)
          .subscribe(
            (statuses) =>
              (this.statuses = statuses.filter((status) => !!status)),
          ),
      );
    }

    this.setValidationsOnSession();
    this.subscriptions.add(
      this.formGroup.controls["status"].valueChanges.subscribe((x) => {
        this.preprocessReason(x);
        this.setValidationsOnStatuses(x);
      }),
    );
    this.initAllowableUserItem();
    this.initAllowableActivityItem();

    this.subscriptions.add(
      this.translateService
        .stream(`components.editRequest.titles.${this.collapseItemName}`)
        .subscribe((title) => (this.sectionTitle = title)),
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private createForm(): void {
    this.formGroup = this.fb.group({
      status: [""],
      reason: [""],
      internalComment: [""],
      photosGallery: [""],
      allowableUser: [""],
      allowableActivity: [""],
    });
  }

  private setValidationsOnStatuses(statusCode: string): void {
    if (!statusCode) {
      return;
    }

    // add validation rule in level of status
    const validationRules = this.statusService.getValidations(statusCode);

    Object.keys(validationRules).forEach((controlName) => {
      const control: UntypedFormControl = <UntypedFormControl>(
        this.formGroup.get(controlName)
      );
      control.clearValidators();

      if (validationRules[controlName]) {
        control.setValidators([Validators.required]);
      }

      setTimeout(() => control.updateValueAndValidity(), 10);
    });
  }

  private setValidationsOnSession(): void {
    if (!this.collapseItem) {
      return;
    }

    const validations = this.collapseItem.getValidations();
    Object.keys(validations).forEach((controlName) => {
      const control: UntypedFormControl = <UntypedFormControl>(
        this.formGroup.get(controlName)
      );

      if (control && validations.getValidationByName(controlName)) {
        control.setValidators([Validators.required]);
        setTimeout(() => control.updateValueAndValidity(), 10);
      }
    });
  }

  private preprocessReason(statusCode: string): void {
    this.subscriptions.add(
      this.commentService
        .getPredefinedTextByStatusCode(statusCode)
        .subscribe((comments) => {
          this.comments = comments.sort((first: Comment, second: Comment) =>
            first.name.localeCompare(second.name),
          );
          const reasonControl: UntypedFormControl = <UntypedFormControl>(
            this.formGroup.controls["reason"]
          );

          if (this.comments.length === 1) {
            reasonControl.setValue(this.comments[0].code);
          } else {
            reasonControl.setValue("");
          }
        }),
    );
  }

  public cancelCollapseItemEdit(): void {
    this.exitInProgress = true;

    if (this.redirectRoute) {
      void this.router.navigate([this.redirectRoute]);
    } else {
      void this.router.navigate(
        [this.previousPageService.previousUrl],
        this.previousPageService.extras,
      );
    }

    this.editRequestService.toggleCollapse(this.collapseItemName);
  }

  public submit() {
    if (!this.request.id) {
      return;
    }
    this.exitInProgress = true;

    if (this.collapseItemName === CollapseItemsEnum.takeInCharge) {
      this.coreService.startSpinner();
      this.takeInCharge()
        .pipe(this.navigateToRedirectOrPreviousRoute(), this.autoUnsubscribe())
        .subscribe({ error: (_) => (this.exitInProgress = false) });
      return;
    }

    if (this.collapseItemName === CollapseItemsEnum.updateResponsible) {
      this.coreService.startSpinner();
      this.updateResponsible()
        .pipe(this.navigateToRedirectOrPreviousRoute(), this.autoUnsubscribe())
        .subscribe();
      return;
    }

    if (this.collapseItemName === CollapseItemsEnum.updateActivity) {
      this.coreService.startSpinner();
      this.updateActivity()
        .pipe(this.navigateToRedirectOrPreviousRoute(), this.autoUnsubscribe())
        .subscribe();
      return;
    }

    this.confirmDialogService.createConfirmDialog({
      confirmedCB: () => {
        this.coreService.startSpinner();
        return of(null).pipe(
          switchMap((x) => {
            if (this.collapseItemName === CollapseItemsEnum.returnDDS) {
              return this.returnDDS();
            }
            let success = of([null]);
            if (this.photosGallery) {
              success = this.photosGallery.saveMedia();
            }
            return forkJoin([success, this.updateStatus()]);
          }),
          this.navigateToRedirectOrPreviousRoute(),
          this.autoUnsubscribe(),
        );
      },
      cancelledCB: async () => {
        this.exitInProgress = false;
      },
    });
  }

  private navigateToRedirectOrPreviousRoute() {
    return tap((x) => {
      if (this.redirectRoute) {
        return this.router.navigate([this.redirectRoute]);
      } else {
        return this.router.navigate(
          [this.previousPageService.previousUrl],
          this.previousPageService.extras,
        );
      }
    });
  }

  private autoUnsubscribe() {
    return first();
  }

  private updateStatus() {
    const formValues = this.formGroup.getRawValue();
    const event: EventRequestById = {
      requestId: this.request.id,
      statusCode: formValues.status || this.request.status.code,
      reason: formValues.reason,
      internalComment: formValues.internalComment,
      type:
        this.collapseItemName === CollapseItemsEnum.addInternalComment
          ? "C"
          : "S",
    };

    return this.editRequestService.updateStatus(event).pipe(
      tap(() => {
        if (
          this.collapseItemName === CollapseItemsEnum.closeWorkorder ||
          this.collapseItemName === CollapseItemsEnum.changeStatus
        ) {
          this.storageService.disabledTracking();
        }
      }),
      switchMap(() => {
        if (
          event.statusCode === STATUS_ENUM.TERMINATED ||
          event.statusCode === STATUS_ENUM.REFUSED
        ) {
          return this.requestService.getGeojsonObservable();
        }
        return of(null);
      }),
      switchMap(() => {
        if (
          !(
            event.statusCode === STATUS_ENUM.TERMINATED ||
            event.statusCode === STATUS_ENUM.REFUSED
          )
        ) {
          return this.editRequestService.reloadRequest(this.request);
        }
        return of(null);
      }),
      tap(() => {
        if (
          !(
            event.statusCode === STATUS_ENUM.TERMINATED ||
            event.statusCode === STATUS_ENUM.REFUSED
          )
        ) {
          this.cancelCollapseItemEdit();
        }
      }),
    );
  }

  private returnDDS(): Observable<IGeojsonRequest> {
    const document = new ReturnDDSDocument();
    const formValues = this.formGroup.getRawValue();
    document.serviceCode = "requestReturn";
    document.internalComment = formValues.internalComment;
    document.reason = formValues.reason;
    document.requestType = this.request.source;

    return this.editRequestService.returnDDS(this.request.id, document).pipe(
      tap((x) => this.storageService.disabledTracking()),
      switchMap((x) => this.requestService.getGeojsonObservable()),
    );
  }

  private takeInCharge(): Observable<any> {
    if (!this.storageService.getTrackedRequestId()) {
      this.storageService.savePositionList(this.request.id.toString());
    }

    const userCode = this.formGroup.controls.allowableUser.value;
    return this.editRequestService
      .takeInCharge([this.request.id], userCode)
      .pipe(
        tap((res: IApiErrorSuccessDTO) => {
          this.storageService.savePositionList(res.success[0].dddId.toString());
        }),
        switchMap((x) => this.requestService.getGeojsonObservable()),
      );
  }

  private updateResponsible(): Observable<any> {
    const userCode = this.formGroup.controls.allowableUser.value;
    return this.editRequestService
      .changeResponsible([this.request.id], userCode, this.request.source)
      .pipe(
        tap((x) =>
          this.editRequestService.toggleCollapse(this.collapseItemName),
        ),
        switchMap((x) => this.editRequestService.reloadRequest(this.request)),
      );
  }

  private updateActivity(): Observable<any> {
    return this.editRequestService
      .changeActivity(
        [this.request.id],
        this.request.source,
        this.formGroup.controls.allowableActivity.value,
      )
      .pipe(
        switchMap((x) => {
          if (
            this.request.source === RequestSourceTypeEnum.workorderRequest &&
            !this.isInternalWorkorderRequestPipe.transform(this.request)
          ) {
            return this.requestService
              .cancelTakeInCharge([this.request.id])
              .pipe(
                map((res) => {
                  return {
                    id: res?.success?.[0]?.ddsId,
                    source: RequestSourceTypeEnum.serviceRequest,
                  };
                }),
              );
          }
          return of({ id: this.request.id, source: this.request.source });
        }),
        tap((_) => {
          this.editRequestService.toggleCollapse(this.collapseItemName);
        }),
        tap((res) => {
          this.ensureCorrectRequestId(res);
        }),
        switchMap((res) => this.editRequestService.reloadRequest(this.request)),
      );
  }

  private ensureCorrectRequestId(res: IRequestIdentifier) {
    if (
      !(
        res.source === RequestSourceTypeEnum.serviceRequest &&
        this.request.source === RequestSourceTypeEnum.workorderRequest
      )
    ) {
      return;
    }
    // This request changed from workorderRequest to serviceRequest
    // Ensure to set correct id so to reload serviceRequest instead of (deleted) workorderRequest
    this.request.source = RequestSourceTypeEnum[res.source.toString()];
    this.request.id = res.id;
  }

  private initAllowableUserItem(): void {
    if (
      this.collapseItemName === CollapseItemsEnum.takeInCharge ||
      this.collapseItemName === CollapseItemsEnum.updateResponsible
    ) {
      this.allowableUsersList =
        this.currentUserService.getAllowableUsersForAGroup(
          this.request.responsible.groupId,
        );
      this.formGroup.controls.allowableUser.setValidators(Validators.required);

      if (this.collapseItemName === CollapseItemsEnum.takeInCharge) {
        this.assignAllowableUserText = "components.editRequest.assignDDS";
      } else {
        this.assignAllowableUserText =
          "components.editRequest.chooseNewResponsible";
      }
    }
  }

  private initAllowableActivityItem(): void {
    if (this.collapseItemName === CollapseItemsEnum.updateActivity) {
      this.allowableActivitiesList =
        this.currentUserService.getAllowableActivities4BelongingBorough();
      this.formGroup.controls.allowableActivity.setValidators(
        Validators.required,
      );
    }
  }

  public getSelectedActivity(activity: IAllowableActivity) {
    this.formGroup.controls.allowableActivity.setValue(activity);
  }
}
